import {Link, useNavigate} from "react-router-dom";
import InstanceTable from "../../components/data_table/instance/InstanceTable.tsx";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import type {Instance} from "../../lib/ModelTypes.ts";
import {instance_manager, InstanceAPIConnectorError} from "../../lib/InstanceManager.ts";
import {swal_util} from "../../lib/SwalUtil.ts";
import Loading from "../Loading.tsx";
import {frontBaseUrl} from "../../build_constants.ts";
import {Navbar} from "../../components/Navbar.tsx";
import {InstanceRowEventHandler} from "./InstanceRowEventHandler.ts";
import type {InstanceDataRef} from "../../components/data_table/instance/type.ts";
import {et} from "../../locales/i18n.tsx";
import { useTranslation } from "react-i18next";


interface ContentProps {
    toggleSidebar: () => void;
}

export interface LoadingProps {
    dataLoading: boolean;
    pageLoading: boolean;
    loadingText: string;
}

const VmInstancePage = ({toggleSidebar}: ContentProps) => {
    const { t, i18n } = useTranslation("ui");

    const fetchCalledRef = useRef(false);
    const dataTableRef = useRef<InstanceDataRef>(null);
    const [data, setData] = useState<LoadingProps>({
        dataLoading: true,
        pageLoading: false,
        loadingText: "",
    });
    const navigate = useNavigate();
    const instanceDataRef = useRef<Instance[]>([]);
    const autoReloadInterval = useRef<ReturnType<typeof setTimeout> | null>(null);
    const instanceRefreshInterval = 3000; // 인스턴스 자동 갱신 타입 ms 단위.

    const instanceRowEventHandlers = useMemo(
        () => new InstanceRowEventHandler(navigate), [navigate]
    );


    async function onClickInstanceDelete() {
        const selectedRows = dataTableRef.current?.getSelectedData() || [];
        if (selectedRows.length > 0) {
            const confirm = await swal_util.show_question(t("instance.deleteTitle"), t("instance.deleteConfirm"));
            if (confirm.isConfirmed) {
                setData((prev) => ({...prev, pageLoading: true}))
                setData((prev) => ({...prev, loadingText: t("instance.deleting")}));

                for (const data of selectedRows) {
                    try {
                        await instance_manager.deleteInstance(data.id);
                    } catch (err) {
                        setData((prev) => ({...prev, pageLoading: false}));
                        if( err instanceof InstanceAPIConnectorError) {
                            await swal_util.show_error(et(err.cause));
                            return;
                        }

                        await swal_util.show_error(t("instance.errors.deleteRequestFailed"));
                        return;
                    }
                }
                await swal_util.show_success(t("instance.deleteRequestSucceeded"));
                setData((prev) => ({...prev, pageLoading: false}))
                // location.reload();
            }
        } else {
            await swal_util.show_warning(t("instance.noSelection"));
        }
    }

    function setPageLoading(loading: boolean) {
        setData((prev) => ({...prev, pageLoading: loading}))
    }

    function setLoadingText(text: string) {
        setData((prev) => ({...prev, loadingText: text}))
    }

    const init = useCallback(async ()  => {
        try {
            await instance_manager.prepare();
            try {
                await instance_manager.getInstanceList("VM");
                await invalidateInstanceList();
                setData(prev => ({...prev, instanceData: instance_manager.instances}));
                setData(prev => ({...prev, dataLoading: false}));

            } catch (err) {
                await swal_util.show_error(t("instance.errors.fetchList"));
                console.error(err);
            }
        } catch (err) {
            await swal_util.show_error(t("instance.errors.prepare"));
            console.error(err);
        }
    }, [setData]);

    async function invalidateInstanceList ()  {
        try {
            await instance_manager.getInstanceList("VM");
            instanceDataRef.current.length = 0; // 기존 인스턴스 목록 모두 삭제
            instanceDataRef.current.push(...instance_manager.instances);
            dataTableRef.current?.invalidate();
        } catch (e) {
            console.error(e);
            if (autoReloadInterval.current) {
                clearInterval(autoReloadInterval.current as unknown as number);
            }
        }
    }

    const startAutoRefreshInstanceList = useCallback((instanceRefreshInterval: number) => {
        let refreshRunning = false;

        autoReloadInterval.current = setInterval(async () => {
            if (refreshRunning) {
                console.log("ignore instance list refreshing");
                return
            }

            refreshRunning = true;
            await invalidateInstanceList();
            refreshRunning = false;

        }, instanceRefreshInterval);
    }, []);

    useEffect(() => {
        if (fetchCalledRef.current) return;
        fetchCalledRef.current = true;
        init().finally();
        startAutoRefreshInstanceList(instanceRefreshInterval);
        return () => {
            const intervalId = autoReloadInterval.current;

            if (!intervalId) return;
            clearInterval(intervalId);
        }
    }, [init, startAutoRefreshInstanceList]);

    return (
        <div>
            {data.pageLoading && <Loading text={data.loadingText}/>}
            {
                <main className="d-block col ps-2 pt-3 min-vh-100">
                    <Navbar toggleSidebar={toggleSidebar}/>
                    <article className="blueshift_main" id="wrapper">
                        <div className="p-4 pt-2">
                            <div className="row">
                                <h6 className="text-primary pt-2 col-6">{t("title.armVmInstance")}</h6>
                                <div className="text-end col-6">
                                    <Link to={`${frontBaseUrl}/vm-instance/new`}
                                          className="button_blue text-decoration-none rounded-3 mx-1 py-2"
                                          style={{fontSize: "14px"}}>{t("instance.create")}</Link>
                                    <button type="button"
                                            className="button_red rounded-3 py-2" style={{fontSize: "14px"}}
                                            onClick={onClickInstanceDelete}>
                                        {t("instance.delete")}
                                    </button>
                                </div>
                            </div>
                            <div className="text-center align-items-center">
                                <InstanceTable
                                    key={`inst-${i18n.resolvedLanguage}`}
                                    instances={instanceDataRef.current}
                                    dataLoading={data.dataLoading}
                                    ref={dataTableRef}
                                    setPageLoading={setPageLoading}
                                    setLoadingText={setLoadingText}
                                    instanceRowEventHandlers={instanceRowEventHandlers}
                                />
                            </div>
                        </div>
                    </article>
                </main>
            }
        </div>
    );
}

export default VmInstancePage;